import io from "socket.io-client";

const socketConnection = process.env.REACT_APP_APPLICATION_MOOD === "production"
  ? process.env.REACT_APP_SOCKET_URL_PRODUCTION
  : process.env.REACT_APP_SOCKET_URL_LOCAL;
const socket = io(socketConnection);


// Store connection callbacks
let connectionCallbacks = [];
let isSocketSetup = false;

/**
 * Setup socket connection and handle 'connect' event.
 */
const setupSocket = () => {
  if (isSocketSetup) return;
  isSocketSetup = true;

  socket.on("connect", () => {
    console.log(`✅ Frontend Socket Connected: ${socket.id} - Connected to backend socket successfully`);
    // Execute all pending connection callbacks
    connectionCallbacks.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error("Error in connection callback:", error);
      }
    });
    connectionCallbacks = []; // Clear callbacks after execution
  });
  
  socket.on("connect_error", (error) => {
    console.error(`❌ Frontend Socket Connection Error: ${error.message}`);
  });
  
  socket.on("disconnect", (reason) => {
    console.log(`❌ Frontend Socket Disconnected: ${reason}`);
    if (reason === "io server disconnect") {
      // Server disconnected, reconnect manually
      console.log("🔄 Attempting to reconnect to socket...");
      socket.connect();
    }
  });
};

/**
 * Execute function when socket is connected, or immediately if already connected
 */
const whenConnected = (callback) => {
  if (socket.connected) {
    callback();
  } else {
    connectionCallbacks.push(callback);
  }
};

/**
 * Disconnect from the socket.
 */
const socketDisconnect = () => {
  if (socket.connected) {
    socket.disconnect();
    console.log("🔌 Socket disconnected manually");
  }
};

/**
 * Get socket connection status
 */
const getSocketStatus = () => {
  return {
    connected: socket.connected,
    id: socket.id,
    transport: socket.io.engine.transport.name
  };
};

/**
 * Subscribe to 'newNotification' events.
 * @param {Function} callback - Callback function to handle new notifications.
 */
const subscribeToNotifications = (callback) => {
  socket.on("newNotification", callback);
  return () => socket.off("newNotification", callback);
};

/**
 * Subscribe to canonical notification stream emitted by backend.
 * This keeps frontend handling consistent across different notification types.
 */
const subscribeToUnifiedNotifications = (callback) => {
  socket.on("notification:new", callback);
  return () => socket.off("notification:new", callback);
};

/**
 * Unsubscribe from 'newNotification' events.
 */
const unsubscribeFromNotifications = () => {
  socket.off("newNotification");
};

/**
 * Add a new employee.
 * @param {string} empId - Employee ID.
 * @param {string} fullName - Full name of the employee.
 * @param {Object} leadDetails - Object containing details of the assigned lead
 */
const addEmp = (empId, fullName) => {
  if (socket.connected) {
    socket.emit("addEmp", { empId, fullName });
    console.log(`👤 Employee added to socket: ${fullName} (${empId})`);
  } else {
    console.warn("⚠️ Socket not connected, cannot add employee");
  }
};

const assignLeadNotification = (data) => {
  if (socket.connected) {
    socket.emit("leadAssign", data);
    console.log(`📤 Lead assignment notification sent:`, data);
  } else {
    console.warn("⚠️ Socket not connected, cannot send lead assignment notification");
  }
}

const unSubLeadAssigned = () => {
  socket.off("leadAssigned");
};

/**
 * Unsubscribe from 'policyNotification' events.
 */
const unSubNewPolicy = () => {
  socket.off("broadcastPolicyMessage");
};

/**
 * Fetch notifications for a specific employee.
 * @param {string} empId - Employee ID.
 */
const postgetNotify = (empId) => {
  if (socket.connected) {
    socket.emit("getNotify", { empId });
    console.log(`📤 Get notifications request sent for employee: ${empId}`);
  } else {
    console.warn("⚠️ Socket not connected, cannot request notifications");
  }
};

/**
 * Unsubscribe from 'getNotify' events.
 */
const unsubPostGetNotify = () => {
  socket.off("getNotify");
};

/**
 * Get the count of notifications.
 * @param {Function} callback - Callback function to handle notification count.
 */
const notificationCount = (callback) => {
  socket.on("notificationCount", callback);
  return () => socket.off("notificationCount", callback);
};

const duplicateEmployee = (empId, type, message) => {
  if (socket.connected) {
    socket.emit("searchClientNotification", { empId, type, message });
    console.log(`📤 Client search notification sent:`, { empId, type, message });
  } else {
    console.warn("⚠️ Socket not connected, cannot send client search notification");
  }
}

const broadcastPolicyMessage = (callback) => {
  socket.on("broadcastPolicyMessage", callback);
  return () => socket.off("broadcastPolicyMessage", callback);
};

const broadcastAssignLeadMessage = (callback) => {
  socket.on("leadAssigned", callback);
  return () => socket.off("leadAssigned", callback);
};
const broadcastPaymentInitiateMessage = (callback) => {
  socket.on("paymentInitiation", callback);
  return () => socket.off("paymentInitiation", callback);
};
const broadcastPaymentVerificationMessage = (callback) => {
  socket.on("paymentVerificationNotification", callback);
  return () => socket.off("paymentVerificationNotification", callback);
};

const meetingNotification = (callback) => {
  socket.on("meetingNotification", callback);
  return () => socket.off("meetingNotification", callback);
}
const feedbackNotification = (callback) => {
  socket.on("newFeedbackOnLead", callback);
  return () => socket.off("newFeedbackOnLead", callback);
}
const facebookLeadNotification = (callback) => {
  socket.on("facebookLeadNotification", callback);
  return () => socket.off("facebookLeadNotification", callback);
}

const clientSearchNotification = (callback) => {
  const handler = (data) => {
    console.log(`📨 Client search notification received:`, data);
    console.log(`🔔 Socket connection status:`, socket.connected);
    console.log(`🆔 Socket ID:`, socket.id);
    callback(data);
  };
  socket.on("clientSearchNotification", handler);
  return () => socket.off("clientSearchNotification", handler);
}

/**
 * Subscribe to race report hourly updates
 * @param {Function} callback - Callback function to handle race report updates
 */
const subscribeToRaceReportUpdates = (callback) => {
  const handler = (data) => {
    console.log(`🏁 Race report update received:`, data);
    callback(data);
  };
  socket.on("race-report:hourly-update", handler);
  return () => socket.off("race-report:hourly-update", handler);
};

/**
 * Unsubscribe from race report updates
 */
const unsubscribeFromRaceReportUpdates = () => {
  socket.off("race-report:hourly-update");
};

export {
  setupSocket,
  socketDisconnect,
  getSocketStatus,
  subscribeToNotifications,
  subscribeToUnifiedNotifications,
  unsubscribeFromNotifications,
  addEmp,
  feedbackNotification,
  facebookLeadNotification,
  clientSearchNotification,
  assignLeadNotification,
  unSubLeadAssigned,
  unSubNewPolicy,
  postgetNotify,
  notificationCount,
  unsubPostGetNotify,
  duplicateEmployee,
  broadcastPolicyMessage,
  broadcastAssignLeadMessage,
  meetingNotification,
  broadcastPaymentInitiateMessage,
  broadcastPaymentVerificationMessage,
  subscribeToRaceReportUpdates,
  unsubscribeFromRaceReportUpdates,
  socket,
  whenConnected
};